/**
 * prepareImageForUpload — MEASURE a seller's image before it goes to `POST /api/assets/images`.
 * It no longer touches the pixels.
 *
 * UNTIL FEATURE 0067 (2026-09-22) this file re-encoded the file in the browser: decode, shrink to
 * 2560 px, WebP at 0.92 through the canvas. That was the first of three lossy generations a product
 * photo went through before a customer saw it, and it threw away every pixel above 2560 for ever.
 * Measured on a real seller photo (SSIMULACRA2, 960 px rendition): the canvas step alone cost 1.6
 * points of fidelity and saved nothing on the wire (18 KB vs 19 KB), because the server re-cut the
 * rendition anyway.
 *
 * The rule now: the server keeps the seller's bytes as the MASTER, untouched, and derives every rendition
 * from them, once. So the browser sends the file exactly as picked. What it still does here is cheap and
 * useful: decode the header to learn the displayed dimensions (EXIF orientation honoured), so the caller
 * can size a placeholder before the upload lands.
 *
 * The cost this moves back onto the uplink is real — a 5 MB phone JPEG at 200 KB/s is ~25 s — which is
 * why every caller shows REAL upload progress (XHR events in the builder, HttpClient events in the
 * dashboard) and the builder uploads in the background. A seller watches a bar, not a spinner.
 *
 * SAFETY RAILS, unchanged: this never throws. Whatever goes wrong while measuring, the ORIGINAL file is
 * handed back and the authoritative server pipeline does its job.
 */
/**
 * The long edge the server used to cap the stored picture at. Kept exported because callers and tests
 * referenced it; it no longer bounds anything — the master keeps every pixel.
 * @deprecated since 0067: the server stores the original; nothing is shrunk client-side any more.
 */
export const UPLOAD_MAX_EDGE = 2560;
/**
 * The canvas WebP quality the browser used to re-encode at.
 * @deprecated since 0067: nothing is re-encoded client-side any more.
 */
export const UPLOAD_WEBP_QUALITY = 0.92;
/**
 * The size an image of `w`x`h` takes once bounded to `maxEdge` — never upscaling. Pure geometry, still
 * used by callers that size a placeholder box.
 */
export function boundedSize(w, h, maxEdge) {
    const longest = Math.max(w, h);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale)),
        scale,
    };
}
/** `photo (2).JPG` → `photo (2).webp`. Keeps the seller's name recognisable in the media list. */
export function withExtension(name, ext) {
    const trimmed = name.trim() || 'image';
    const dot = trimmed.lastIndexOf('.');
    const stem = dot > 0 ? trimmed.slice(0, dot) : trimmed;
    return `${stem}.${ext}`;
}
/** Formats the browser may be able to decode for a size reading. GIF is skipped: a bitmap of it is frame one, and its size is not worth a decode. */
const MEASURABLE = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/heic',
    'image/heif',
    'image/avif',
]);
/**
 * Prepare `file` for `POST /api/assets/images`: the file itself, plus its displayed size when the browser
 * can read it. Never throws.
 */
export async function prepareImageForUpload(file, _opts = {}) {
    const untouched = {
        file,
        recompressed: false,
        originalBytes: file.size,
        bytes: file.size,
    };
    if (!MEASURABLE.has(file.type))
        return untouched;
    if (typeof createImageBitmap !== 'function')
        return untouched;
    let bitmap;
    try {
        // `from-image` honours the EXIF orientation tag, so a portrait phone shot reports its DISPLAYED
        // size — the same the server will announce once it has stored the master.
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    }
    catch {
        return untouched;
    }
    try {
        return { ...untouched, width: bitmap.width, height: bitmap.height };
    }
    finally {
        bitmap.close?.();
    }
}
//# sourceMappingURL=prepareImage.js.map